import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Shield, Clock, TrendingUp, AlertTriangle, Lock, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import MapPinArt from "../components/MapPinArt";

function fmtRemaining(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function MyMapPin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [equipping, setEquipping] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/map-pins/me");
      setState(data);
    } catch (e) {
      toast.error("Couldn't load your pin state");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const equip = async (pin_key) => {
    setEquipping(pin_key);
    try {
      const { data } = await api.post("/map-pins/equip", { pin_key });
      setState(data.state);
      toast.success(`${data.state.equipped.name} equipped`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't equip that pin");
    } finally {
      setEquipping(null);
    }
  };

  if (loading || !state) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center text-zinc-500" data-testid="map-pin-loading">
        Loading your map pin…
      </div>
    );
  }

  const equipped = state.equipped;
  const nextTier = state.next_tier;
  const graceRem = fmtRemaining(state.grace_until);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24 space-y-5" data-testid="my-map-pin-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="map-pin-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-pink-400/30 bg-gradient-to-br from-pink-500/10 via-zinc-950 to-cyan-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-pink-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Shield className="w-3 h-3" /> Shop Customization
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">My Map Pin</h1>
        <p className="text-sm text-zinc-400 mt-1">Your reputation earns you premium Hidden Gem markers on the map.</p>
      </header>

      {/* Currently equipped */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5" data-testid="map-pin-equipped-section">
        <div className="flex items-start gap-5">
          <MapPinArt tier={equipped.key} size={104} selected />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Currently equipped</p>
            <h2 className="text-2xl font-black tracking-tight mt-0.5" data-testid="map-pin-equipped-name">{equipped.name}</h2>
            <p className="text-xs text-zinc-400 mt-0.5">{equipped.label}</p>
            <p className="text-xs text-zinc-300 mt-2 inline-flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-cyan-300" />
              Your reputation: <span className="font-black text-white" data-testid="map-pin-score">{state.score}</span>
              <span className="text-zinc-500">· maintain {equipped.maintain_min}+</span>
            </p>
            {nextTier && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold">
                  <span className="text-zinc-400">Next: {nextTier.name}</span>
                  <span className="text-cyan-300" data-testid="map-pin-next-remaining">{nextTier.to_next} to go</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-1">
                  <div
                    className="h-full bg-gradient-to-r from-pink-500 to-cyan-400"
                    style={{
                      width: `${Math.max(2, Math.min(100, Math.round((state.score - equipped.maintain_min) / Math.max(1, nextTier.min - equipped.maintain_min) * 100)))}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {state.at_risk && (
          <div className="mt-4 rounded-2xl border border-amber-400/50 bg-amber-500/10 p-3 text-amber-100 text-sm" data-testid="map-pin-at-risk-banner">
            <div className="inline-flex items-center gap-1.5 font-black">
              <AlertTriangle className="w-4 h-4" /> {equipped.name} is at risk
            </div>
            <p className="text-xs mt-1 text-amber-100/85">
              Your reputation is below {equipped.maintain_min}. Restore it within <span className="font-black">{graceRem}</span> to keep this pin — otherwise it will automatically downgrade to your highest eligible tier.
            </p>
          </div>
        )}
      </section>

      {/* All tiers list */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5" data-testid="map-pin-all-tiers">
        <h3 className="text-sm font-black tracking-tighter inline-flex items-center gap-1.5 mb-3">
          <Sparkles className="w-4 h-4 text-pink-300" /> All map pins ({state.pins.length})
        </h3>
        <div className="grid grid-cols-1 gap-2">
          {state.pins.map((p) => {
            const locked = !p.is_eligible;
            const isEquipped = p.is_equipped;
            const disabled = p.is_disabled;
            return (
              <div
                key={p.key}
                className={`rounded-2xl border p-3 flex items-center gap-4 transition-colors ${
                  isEquipped
                    ? "border-pink-400/50 bg-pink-500/10"
                    : locked
                      ? "border-white/10 bg-white/[0.02]"
                      : "border-emerald-400/30 bg-emerald-500/[0.06]"
                }`}
                data-testid={`map-pin-row-${p.key}`}
              >
                <MapPinArt tier={p.key} size={64} locked={locked} selected={isEquipped} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-base font-black tracking-tight" data-testid={`map-pin-row-name-${p.key}`}>{p.name}</p>
                    {isEquipped && (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-pink-500/20 text-pink-200 border border-pink-400/40 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Equipped
                      </span>
                    )}
                    {disabled && (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-zinc-800 text-zinc-400 border border-white/10 rounded-full px-2 py-0.5">
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    {p.label} · requires {p.min}+ reputation
                  </p>
                  {locked && !disabled && (
                    <p className="text-[10px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> {p.min - state.score} more to unlock
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  {isEquipped ? (
                    <span className="text-[10px] uppercase tracking-widest text-pink-300 font-black">Active</span>
                  ) : disabled ? (
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Off</span>
                  ) : locked ? (
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Locked</span>
                  ) : (
                    <Button
                      onClick={() => equip(p.key)}
                      disabled={equipping === p.key}
                      className="h-8 px-3 rounded-full bg-pink-500 hover:bg-pink-400 text-xs font-bold"
                      data-testid={`map-pin-equip-${p.key}`}
                    >
                      {equipping === p.key ? "Equipping…" : "Equip"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <p className="text-[11px] text-zinc-500 leading-relaxed px-1">
        <Clock className="w-3 h-3 inline mr-1" />
        If your reputation drops below the requirement for your equipped pin, a
        <span className="text-zinc-300 font-bold"> {state.grace_days}-day grace period</span> starts. Restore
        it to keep your pin — otherwise it will auto-downgrade to your highest eligible tier.
      </p>
    </div>
  );
}
